/**
 * Shared viewport media-query hooks.
 *
 * `useIsMobile` is THE phone-layout switch for the whole app — it matches the
 * `MOBILE_BREAKPOINT` used by the workspace's single-pane layout and by every
 * `@media (max-width: 767px)` rule in Workspace.css, so JS branching and CSS
 * breakpoints can never disagree. Extracted from Workspace.tsx so panels and
 * modals (PanelModal, ConversationMenu, HealthBanner, …) can share it.
 *
 * @module
 */

import { useEffect, useState } from 'react'

import { MOBILE_BREAKPOINT } from '../utilities/workspace-responsive.js'

/**
 * Subscribes to a CSS media query. SSR/prerender-safe (no `window` access
 * until mounted; reports `false` until then).
 * @param query - A media query string, e.g. `'(max-width: 767px)'`.
 * @returns Whether the query currently matches.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent): void => setMatches(e.matches)
    setMatches(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return matches
}

/**
 * Tracks whether the viewport is phone-width (`< MOBILE_BREAKPOINT`) so the IDE
 * can collapse its side-by-side row into a single-pane segmented layout.
 * @returns Whether the single-pane mobile layout should be used.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
}
